import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";

import SupplierService from "../../services/SupplierService";
import CustomerAutoComplete from "../../components/CustomerAutoComplete";

const initialForm = {
  name: "",
  taxNumber: "",
  taxOffice: "",
  phoneNumber: "",
  email: "",
  bankInfo: "",
  address: "",
  openingBalance: 0,

  useDueTracking: false,
  defaultDueDay: null,
  paymentAllocationMethod: 1,
  relatedCustomerId: null,
  saveAsCustomer: false,
};

const paymentAllocationMethods = [
  {
    label: "Otomatik dağıt — En eski vadeden başla",
    value: 1,
    description:
      "Tedarikçiye ödeme girildiğinde sistem açık borçları vade tarihine göre otomatik kapatır.",
  },
  {
    label: "Manuel dağıt — Ödeme sırasında ben seçeyim",
    value: 2,
    description:
      "Ödeme kaydederken hangi alış/vade borçlarının kapatılacağını kullanıcı belirler.",
  },
];

const IconBox = ({
  icon,
  active = false,
  dark = false,
  slate = false,
  size = 36,
  iconSize = 15,
  className = "",
}) => {
  let colorsClass = "supplier-iconbox-emerald";

  if (dark) {
    colorsClass = "bg-white/10 border-white/15 text-white";
  } else if (active) {
    colorsClass = "bg-emerald-600 border-emerald-600 text-white";
  } else if (slate) {
    colorsClass = "supplier-iconbox-slate";
  }

  return (
    <div
      className={`flex items-center justify-center border shrink-0 ${colorsClass} ${className}`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: 10,
      }}
    >
      <i
        className={icon}
        style={{
          fontSize: iconSize,
        }}
      />
    </div>
  );
};

const SectionCard = ({ icon, title, description, children }) => {
  return (
    <div className="supplier-section-card rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800/40">
        <div className="flex items-center gap-3">
          <IconBox icon={icon} size={36} iconSize={15} />

          <div>
            <h2 className="text-[18px] font-black text-slate-900 dark:text-white leading-none">
              {title}
            </h2>
            <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">{children}</div>
    </div>
  );
};

const FieldLabel = ({ icon, children }) => {
  return (
    <label className="flex items-center gap-2 text-[12px] font-black text-slate-500 uppercase tracking-widest mb-2">
      {icon && (
        <i
          className={icon}
          style={{
            fontSize: 13,
            color: "#10b981",
          }}
        />
      )}
      {children}
    </label>
  );
};

export default function SuppliersAddPage() {
  const [mounted, setMounted] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isSaving, setIsSaving] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  const goToListPage = () => navigate("/suppliers");

  const setField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDueTrackingChange = (value) => {
    setForm((prev) => ({
      ...prev,
      useDueTracking: value,
      defaultDueDay: value ? prev.defaultDueDay ?? 30 : null,
      paymentAllocationMethod: value ? prev.paymentAllocationMethod || 1 : 1,
    }));
  };
  const paymentAllocationItemTemplate = (option) => {
    return (
      <div className="flex items-start gap-3 py-1">
        <div
          className="flex items-center justify-center border shrink-0 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30 text-emerald-600 dark:text-emerald-400"
          style={{
            width: 30,
            height: 30,
            minWidth: 30,
            minHeight: 30,
            borderRadius: 8,
          }}
        >
          <i
            className={
              option.value === 1 ? "pi pi-sort-amount-down" : "pi pi-list-check"
            }
            style={{
              fontSize: 13,
            }}
          />
        </div>

        <div>
          <div className="text-[14px] font-bold text-slate-800 dark:text-slate-200">
            {option.label}
          </div>
          <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
            {option.description}
          </div>
        </div>
      </div>
    );
  };

  const selectedPaymentAllocationTemplate = (option, props) => {
    if (!option) {
      return <span className="text-slate-400">{props.placeholder}</span>;
    }

    return (
      <div className="flex items-center gap-2">
        <i
          className={`${option.value === 1 ? "pi pi-sort-amount-down" : "pi pi-list-check"
            } text-emerald-600 dark:text-emerald-400`}
          style={{
            fontSize: 13,
          }}
        />
        <span className="text-[14px] font-semibold text-slate-800 dark:text-slate-200">{option.label}</span>
      </div>
    );
  };
  const validateForm = () => {
    if (!form.name?.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Eksik Bilgi",
        detail: "Tedarikçi adı zorunludur.",
        life: 3000,
      });
      return false;
    }

    if (
      form.useDueTracking &&
      (!form.defaultDueDay || form.defaultDueDay <= 0)
    ) {
      toast.current.show({
        severity: "warn",
        summary: "Eksik Bilgi",
        detail: "Vade takibi açıkken varsayılan vade günü girilmelidir.",
        life: 3500,
      });
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    const payload = {
      name: form.name,
      taxNo: form.taxNumber,
      taxOffice: form.taxOffice,
      phone: form.phoneNumber,
      email: form.email,
      bankInfo: form.bankInfo,
      address: form.address,
      openingBalance: form.openingBalance ?? 0,

      useDueTracking: form.useDueTracking,
      defaultDueDay: form.useDueTracking ? form.defaultDueDay : null,
      paymentAllocationMethod: form.useDueTracking
        ? form.paymentAllocationMethod
        : 1,
      relatedCustomerId: form.relatedCustomerId,
      saveAsCustomer: form.saveAsCustomer,
    };

    try {
      const res = await SupplierService.addSupplier(payload);

      toast.current.show({
        severity: "success",
        summary: "Başarılı",
        detail: res?.message || "Tedarikçi başarıyla kaydedildi.",
        life: 3000,
      });

      resetForm();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail:
          error.response?.data?.message ||
          error.message ||
          "Tedarikçi kaydedilirken bir hata oluştu.",
        life: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-900 p-2 md:p-2">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="max-w-7xl mx-auto space-y-3">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-900 p-6 md:p-8 shadow-lg">
          <div
            className="absolute rounded-full bg-emerald-400/10"
            style={{
              right: -40,
              top: -40,
              width: 192,
              height: 192,
            }}
          />

          <div
            className="absolute rounded-full bg-white/5"
            style={{
              right: 80,
              bottom: 0,
              width: 128,
              height: 128,
            }}
          />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-3">
              <IconBox icon="pi pi-truck" dark size={40} iconSize={17} />

              <div>
                <div className="text-[12px] font-black tracking-[0.22em] text-emerald-300 uppercase">
                  Tedarikçi Yönetimi
                </div>
                <h1 className="text-2xl md:text-4xl font-black text-white mt-2">
                  Yeni Tedarikçi Ekle
                </h1>
                <p className="text-[14px] md:text-[15px] text-slate-300 mt-3 max-w-2xl leading-relaxed">
                  Tedarikçi bilgilerini, açılış bakiyesini ve borç/vade takip
                  ayarlarını tek ekrandan tanımlayın.
                </p>
              </div>
            </div>

            <Button
              type="button"
              label="Tedarikçi Listesi"
              icon="pi pi-arrow-left"
              outlined
              className="!rounded-2xl !text-white !border-white/30 hover:!bg-white/10"
              onClick={goToListPage}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <SectionCard
            icon="pi pi-building"
            title="Firma Bilgileri"
            description="Tedarikçinin resmi adı, vergi bilgileri ve iletişim detayları."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <FieldLabel icon="pi pi-briefcase">Tedarikçi Adı</FieldLabel>
                <InputText
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  autoFocus
                  placeholder="Ör: Aydınlar Ltd. Şti."
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-map-marker">Vergi Dairesi</FieldLabel>
                <InputText
                  name="taxOffice"
                  value={form.taxOffice}
                  onChange={handleInputChange}
                  placeholder="Ör: Muratpaşa"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-id-card">
                  Vergi Numarası / T.C. No
                </FieldLabel>
                <InputText
                  name="taxNumber"
                  value={form.taxNumber}
                  onChange={handleInputChange}
                  maxLength={11}
                  placeholder="Ör: 1234567890"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-phone">Telefon</FieldLabel>
                <InputMask
                  name="phoneNumber"
                  mask="(999) 999 99 99"
                  value={form.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(555) 123 45 67"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-envelope">E-posta</FieldLabel>
                <InputText
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  type="email"
                  placeholder="tedarikci@eposta.com"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon="pi pi-wallet"
            title="Cari ve Borç Takibi"
            description="Açılış bakiyesi, vade takip tercihi ve ödeme kapama ayarları."
          >
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
              <div className="xl:col-span-4">
                <div className="h-full rounded-3xl border supplier-card-slate p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <IconBox
                      icon="pi pi-money-bill"
                      slate
                      size={36}
                      iconSize={15}
                    />

                    <div>
                      <div className="text-[15px] font-black text-slate-900 dark:text-white">
                        Açılış Bakiyesi
                      </div>
                      <div className="text-[13px] text-slate-500 dark:text-slate-400">
                        İlk cari borç/alacak durumu
                      </div>
                    </div>
                  </div>

                  <InputNumber
                    value={form.openingBalance}
                    onValueChange={(e) =>
                      setField("openingBalance", e.value ?? 0)
                    }
                    mode="currency"
                    currency="TRY"
                    locale="tr-TR"
                    className="w-full"
                    inputClassName="!h-12 !rounded-xl !text-[15px]"
                  />

                  <div className="mt-4 rounded-2xl border supplier-info-box p-4">
                    <div className="flex items-start gap-3">
                      <i className="pi pi-info-circle text-amber-500 mt-0.5" />
                      <p className="text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed">
                        Tedarikçiye borcunuz varsa pozitif, tedarikçi size
                        borçluysa eksi değer girin.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="xl:col-span-8">
                <div
                  className={`h-full rounded-3xl border p-5 transition-all ${form.useDueTracking
                    ? "supplier-card-emerald"
                    : "supplier-card-slate"
                    }`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <IconBox
                        icon="pi pi-calendar-clock"
                        active={form.useDueTracking}
                        slate={!form.useDueTracking}
                        size={36}
                        iconSize={15}
                      />

                      <div>
                        <div className="text-[17px] font-black text-slate-900 dark:text-white">
                          Vade takipli borç takibi
                        </div>
                        <p className="text-[14px] text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
                          Bu özellik açıkken alış işlemlerinde vade tarihi
                          önerilir. Ödeme yapılırken açık borç kalemleri
                          kapatılarak raporlarda vadesi gelen/geçen borçlar
                          takip edilebilir.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border supplier-info-box px-4 py-3">
                      <span className="text-[13px] font-bold text-slate-600 dark:text-slate-400">
                        {form.useDueTracking ? "Aktif" : "Pasif"}
                      </span>
                      <InputSwitch
                        checked={form.useDueTracking}
                        onChange={(e) => handleDueTrackingChange(e.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                    <div>
                      <FieldLabel icon="pi pi-clock">
                        Varsayılan Vade Günü
                      </FieldLabel>
                      <InputNumber
                        value={form.defaultDueDay}
                        onValueChange={(e) =>
                          setField("defaultDueDay", e.value)
                        }
                        min={1}
                        max={365}
                        disabled={!form.useDueTracking}
                        suffix=" gün"
                        placeholder="Ör: 30"
                        className="w-full"
                        inputClassName="!h-12 !rounded-xl !text-[15px]"
                      />
                      <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                        Alış tarihinden kaç gün sonrası vade tarihi olarak
                        önerilsin?
                      </p>
                    </div>

                    <div>
                      <FieldLabel icon="pi pi-sliders-h">
                        Varsayılan Ödeme Dağıtımı
                      </FieldLabel>

                      <Dropdown
                        value={form.paymentAllocationMethod}
                        options={paymentAllocationMethods}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setField("paymentAllocationMethod", e.value)}
                        disabled={!form.useDueTracking}
                        appendTo={mounted ? document.body : "self"}
                        className="w-full !rounded-xl"
                        panelClassName="supplier-allocation-dropdown-panel"
                        placeholder="Ödeme dağıtım yöntemini seçiniz"
                        itemTemplate={paymentAllocationItemTemplate}
                        valueTemplate={selectedPaymentAllocationTemplate}
                      />

                      <div className="mt-3 rounded-2xl border supplier-info-box-emerald p-4">
                        <div className="flex items-start gap-3">
                          <i
                            className="pi pi-info-circle"
                            style={{
                              fontSize: 15,
                              color: "#059669",
                              marginTop: 2,
                            }}
                          />

                          <div>
                            <div className="text-[13px] font-black text-slate-800 dark:text-white">
                              Bu seçim daha sonra değiştirilebilir.
                            </div>

                            <p className="text-[13px] text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
                              Bu alan sadece tedarikçi için varsayılan ödeme dağıtım şeklini
                              belirler. Tedarikçi düzenleme ekranından daha sonra değiştirilebilir.
                              Ayrıca ödeme girerken, kullanıcı isterse dağıtımı kontrol edip farklı
                              borç kalemlerine yönlendirebilir.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="xl:col-span-12">
                <div className="rounded-3xl border supplier-card-indigo p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <IconBox
                        icon="pi pi-users"
                        size={40}
                        iconSize={18}
                        className="supplier-iconbox-indigo"
                      />
                      <div>
                        <div className="text-[17px] font-black text-slate-900 dark:text-white">
                          İlişkili Müşteri Hesabı
                        </div>
                        <p className="text-[14px] text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
                          Eğer bu tedarikçi aynı zamanda bir müşteriniz ise, hesaplarını birleştirerek karşılıklı mahsuplaşma yapabilirsiniz.
                        </p>
                      </div>
                    </div>
                    <div className="w-full md:w-[400px] flex flex-col gap-2">
                      <CustomerAutoComplete
                        value={form.relatedCustomerId}
                        onChange={(val) => setField("relatedCustomerId", val)}
                        className="w-full"
                        placeholder="Müşteri seçerek bağlayın..."
                      />
                      {!form.relatedCustomerId && (
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-xl border border-emerald-100 dark:border-emerald-900/30 mt-2">
                          <Checkbox inputId="saveAsCustomer" checked={form.saveAsCustomer} onChange={(e) => setField("saveAsCustomer", e.checked)} />
                          <label htmlFor="saveAsCustomer" className="text-sm font-bold text-emerald-800 dark:text-emerald-300 cursor-pointer select-none">Aynı zamanda Müşteri olarak da kaydet</label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon="pi pi-credit-card"
            title="Banka ve Adres Bilgileri"
            description="IBAN, banka açıklaması, sevkiyat veya iletişim adresi gibi ek bilgiler."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <FieldLabel icon="pi pi-credit-card">Banka Bilgisi</FieldLabel>
                <InputTextarea
                  name="bankInfo"
                  value={form.bankInfo}
                  onChange={handleInputChange}
                  rows={5}
                  autoResize
                  placeholder="Banka adı, IBAN veya açıklama"
                  className="w-full !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-home">Adres</FieldLabel>
                <InputTextarea
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  rows={5}
                  autoResize
                  placeholder="Adres bilgisi"
                  className="w-full !rounded-xl !text-[15px]"
                />
              </div>
            </div>
          </SectionCard>

          <div className="sticky bottom-0 z-20 -mx-4 md:-mx-7 px-4 md:px-7 py-4 bg-white/85 dark:bg-slate-950/85 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 text-[13px] text-slate-500">
                <i className="pi pi-shield text-emerald-500" />
                <span>
                  Kayıt sonrası tedarikçi alış ve ödeme işlemlerinde
                  kullanılabilir.
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  label="Temizle"
                  icon="pi pi-refresh"
                  outlined
                  className="!rounded-2xl !h-12 !px-5"
                  onClick={resetForm}
                  disabled={isSaving}
                />

                <Button
                  type="submit"
                  label={isSaving ? "Kaydediliyor..." : "Tedarikçiyi Kaydet"}
                  icon="pi pi-save"
                  className="!rounded-2xl !h-12 !px-6 !bg-emerald-600 !border-emerald-600"
                  loading={isSaving}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}